import { useState } from 'react'
import { Base, toast } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { PbEcommerceStore } from '@itsrever/rever-api'
import { useStores, useSetStoreAddress } from '@/hooks'
import { useParams } from 'react-router'
import { useAppSelector } from '@/redux/hooks'

const {
  Button,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
  DialogCloseButton,
  DialogHeader,
  ComboboxRoot,
  ComboboxInputGroup,
  ComboboxInput,
  ComboboxClear,
  ComboboxList,
  ComboboxEmpty,
  ComboboxLoading,
  ComboboxCheckItem,
  ComboboxChevronTrigger,
  ComboboxDropdown
} = Base

interface SelectStorePopupProps {
  onSubmitStore: () => void
  isLoading: boolean
  customerPrintedOrderId: string | undefined
  ecommerceId: string | undefined
}

const formatStoreLabel = (s: PbEcommerceStore) =>
  [s.name, s.address1].filter(Boolean).join(', ')

export const SelectStoreDialog = ({
  onSubmitStore,
  isLoading,
  customerPrintedOrderId,
  ecommerceId
}: SelectStorePopupProps) => {
  const { t } = useTranslation()

  const email = useAppSelector(
    (store) => store.userApi.getMe.response.user?.name
  )

  const { id } = useParams()

  const { data, isLoading: loadingStores } = useStores({
    ecommerceId,
    orderReference: customerPrintedOrderId
  })

  const { mutate } = useSetStoreAddress()

  const [store, setStore] = useState<PbEcommerceStore | null>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!id) return

    mutate(
      {
        processId: id,
        pbSetStoreAddressRequest: {
          process_id: id,
          user: email,
          address: {
            address_line_1: store?.address1,
            address_line_2: store?.address2,
            phone: store?.phone,
            email: store?.email,
            country_code: store?.country_code,
            city: store?.city,
            state_province_code: store?.state_code,
            postcode: store?.postal_code
          }
        }
      },
      {
        onSuccess: () => {
          onSubmitStore()
        },
        onError: () => {
          toast({
            text: t('products_tab.store_set_error'),
            variant: 'error'
          })
        }
      }
    )
  }

  return (
    <>
      <div className="flex size-full w-[26rem] flex-col">
        <DialogCloseButton />
        <DialogHeader>
          <DialogTitle>{t('products_tab.select_store')}</DialogTitle>
          <DialogDescription>
            {t('products_tab.select_store_description')}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4 pt-4" onSubmit={handleSubmit}>
          <ComboboxRoot<PbEcommerceStore>
            items={data?.stores ?? []}
            value={store}
            onValueChange={setStore}
            itemToStringLabel={formatStoreLabel}
            itemToStringValue={(s: PbEcommerceStore) => s.id ?? ''}
          >
            <ComboboxInputGroup>
              <ComboboxInput
                placeholder={t('products_tab.store_placeholder')}
              />
              <ComboboxClear
                aria-label={t('products_tab.cancel')}
                className="cursor-pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 256 256"
                  className="fill-neutral-400"
                >
                  <path d="M165.66,101.66,139.31,128l26.35,26.34a8,8,0,0,1-11.32,11.32L128,139.31l-26.34,26.35a8,8,0,0,1-11.32-11.32L116.69,128,90.34,101.66a8,8,0,0,1,11.32-11.32L128,116.69l26.34-26.35a8,8,0,0,1,11.32,11.32ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z" />
                </svg>
              </ComboboxClear>
              <ComboboxChevronTrigger
                label={t('products_tab.store_placeholder')}
              />
            </ComboboxInputGroup>
            <ComboboxDropdown>
              {loadingStores ? (
                <ComboboxLoading label={t('products_tab.loading_stores')} />
              ) : (
                <>
                  <ComboboxEmpty>
                    {t('products_tab.no_stores_found')}
                  </ComboboxEmpty>
                  <ComboboxList>
                    {(s: PbEcommerceStore) => (
                      <ComboboxCheckItem key={s.id} value={s}>
                        <span className="font-medium">
                          {formatStoreLabel(s)}
                        </span>
                      </ComboboxCheckItem>
                    )}
                  </ComboboxList>
                </>
              )}
            </ComboboxDropdown>
          </ComboboxRoot>
          <DialogFooter className="mt-auto">
            <DialogClose render={<Button variant="secondary" type="button" />}>
              {t('products_tab.cancel')}
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={!store || loadingStores}
            >
              {t('products_tab.submit')}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </>
  )
}
